import {
  columnFilteringFeature,
  columnVisibilityFeature,
  createFilteredRowModel,
  createSortedRowModel,
  functionalUpdate,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type ColumnFiltersState as TanStackColumnFilters,
  type ColumnVisibilityState,
  type FilterFn,
  type SortFn,
  type SortingState as TanStackSorting,
  type Updater,
} from '@tanstack/react-table';
import {
  Fragment,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link, useNavigate } from 'react-router';

import { APP_LOCALE } from '../../format';
import { EmptyState } from '../states';
import { useStoredHiddenColumns } from './hiddenColumns';

/**
 * The value a column sorts and filters by.
 *
 * `render` returns a ReactNode, which cannot be compared, so a column that
 * wants either capability says what its comparable value is. `null` and
 * `undefined` sort last in both directions and never match a filter.
 */
export type ColumnValue = string | number | Date | null | undefined;

/** One choice of a `select` filter. `value` is matched against the accessor. */
export interface ColumnFilterOption {
  readonly value: string;
  readonly label: string;
}

export type ColumnFilterKind =
  | {
      /** Case-insensitive "contains" on the accessor's value. */
      readonly kind: 'text';
      readonly placeholder?: string;
    }
  | {
      /** The accessor's value equals the chosen option's `value`. */
      readonly kind: 'select';
      readonly options: readonly ColumnFilterOption[];
    };

/** Which column the table is sorted by, if any. One column at a time. */
export type SortingState = readonly { readonly id: string; readonly desc: boolean }[];

/**
 * The filters in force, one entry per filtered column, keyed by `Column.key`.
 * Every value is a string: what was typed into a text filter, or the chosen
 * option's `value` of a select.
 */
export type ColumnFiltersState = readonly { readonly id: string; readonly value: string }[];

/**
 * A table.
 *
 * Columns describe themselves -- including how to render a cell -- so a screen
 * never hand-writes a `<tr>`, and so every table in the product gets the same
 * header semantics, the same alignment rules and the same empty state. The
 * `align` option exists because numbers and dates read wrong when they are not
 * right-aligned, and that is not something to decide per screen.
 *
 * Sorting, filtering and hiding are opt-in per column (#437). TanStack Table
 * holds that state and does the row work; the markup below is still ours, so a
 * table that opts into none of it renders exactly what it did before.
 */
export interface Column<TRow> {
  readonly key: string;
  readonly header: string;
  readonly render: (row: TRow) => ReactNode;
  readonly align?: 'start' | 'end';
  /** Extra description for screen readers when the header alone is terse. */
  readonly headerHint?: string;
  /**
   * The comparable value behind the cell. Required for `sortable` and
   * `filter`: a column without one is neither sortable nor filterable --
   * except on a `manual` table, which never reads it, so a column whose
   * filter the screen applies to the whole row needs no value of its own.
   */
  readonly accessor?: (row: TRow) => ColumnValue;
  /** The header toggles ascending, descending and off. Needs `accessor`. */
  readonly sortable?: boolean;
  /** A control for this column in the filter row. Needs `accessor`, unless `manual`. */
  readonly filter?: ColumnFilterKind;
  /**
   * `false` keeps the column out of reach of the "Colunas" control. The first
   * column of a `rowLink` table can never be hidden, since it holds the anchor.
   */
  readonly hideable?: boolean;
}

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  columnFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  columnVisibilityFeature,
});

type Features = typeof features;

/**
 * What the table holds for each row. TanStack wants its rows to be records;
 * `DataTable` has always accepted any `TRow`, so each one rides in a box.
 */
interface Boxed<TRow> {
  readonly row: TRow;
}

function comparable(value: unknown): string | number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return String(value);
}

/** Ascending only; the table reverses it for a descending sort. */
function compareValues<TRow>(): SortFn<Features, Boxed<TRow>> {
  return (a, b, columnId) => {
    const left = comparable(a.getValue(columnId));
    const right = comparable(b.getValue(columnId));
    if (typeof left === 'number' && typeof right === 'number') return left - right;
    return String(left).localeCompare(String(right), APP_LOCALE, {
      numeric: true,
      sensitivity: 'base',
    });
  };
}

function normalise(text: string): string {
  return text.toLocaleLowerCase(APP_LOCALE);
}

function textFilter<TRow>(): FilterFn<Features, Boxed<TRow>> {
  const filter: FilterFn<Features, Boxed<TRow>> = (row, columnId, filterValue: string) => {
    const value: unknown = row.getValue(columnId);
    if (value === null || value === undefined) return false;
    const text = value instanceof Date ? value.toISOString() : String(value as string | number);
    return normalise(text).includes(normalise(filterValue));
  };
  filter.autoRemove = (value: unknown) => typeof value !== 'string' || value.trim() === '';
  return filter;
}

function selectFilter<TRow>(): FilterFn<Features, Boxed<TRow>> {
  const filter: FilterFn<Features, Boxed<TRow>> = (row, columnId, filterValue: string) => {
    const value: unknown = row.getValue(columnId);
    if (value === null || value === undefined) return false;
    return String(value as string | number) === filterValue;
  };
  filter.autoRemove = (value: unknown) => typeof value !== 'string' || value === '';
  return filter;
}

function canBeHidden<TRow>(column: Column<TRow>, index: number, linked: boolean): boolean {
  return column.hideable !== false && !(linked && index === 0);
}

/**
 * The most keys the server keeps for one table (`HIDDEN_COLUMNS_MAX` in
 * `app/schemas/auth.py`). Far more than any table has columns; only keys that
 * name no column here could ever reach it.
 */
const STORED_KEYS_MAX = 48;

export function DataTable<TRow>({
  id,
  caption,
  columns,
  rows,
  rowKey,
  expansion,
  rowLink,
  emptyTitle = 'Nenhum registro',
  emptyDescription,
  noMatchTitle = 'Nenhum registro corresponde aos filtros',
  noMatchDescription = 'Altere ou limpe os filtros para ver mais linhas.',
  noMatchAction,
  filtered = false,
  columnVisibility = true,
  manual = false,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
}: {
  /** Describes the table to assistive technology. Visually hidden. */
  caption: string;
  columns: readonly Column<TRow>[];
  rows: readonly TRow[];
  rowKey: (row: TRow, index: number) => string;
  /**
   * More about one row than a grid can hold, in a full-width row under it.
   * Return `null` for the rows that are not open.
   *
   * It is here rather than left to each screen because the alternative -- a
   * panel somewhere else on the page -- puts the detail away from the line it
   * belongs to, and a reader comparing twenty lines then has to remember which
   * one they opened. The row stays where it was and grows.
   */
  expansion?: (row: TRow) => ReactNode;
  /**
   * Where a row leads, for a table whose only action on a row is opening it.
   *
   * The first column's content becomes the row's one real anchor -- so it is
   * named by what the row is, tabbing moves row to row, and Ctrl/Cmd-click or
   * a middle click opens a new tab as on any link -- and a click anywhere else
   * on the row goes to the same address. Every cell is deliberately *not* an
   * anchor: that would be five links per row to the same place for anything
   * reading the page's destinations. A click on a control inside the row is
   * left to that control.
   */
  rowLink?: (row: TRow) => string;
  emptyTitle?: string;
  emptyDescription?: string;
  /**
   * What the body says when filters leave no row, under the filter row so the
   * filters stay where they can be changed. A screen whose filters reach
   * further than the table's own -- a search box above it, say -- names them
   * and offers its own way to clear them.
   */
  noMatchTitle?: string;
  noMatchDescription?: string;
  noMatchAction?: ReactNode;
  /**
   * The rows are a filtered result even when the filter row is blank -- in
   * `manual` mode the rows on screen can still be the answer to filters the
   * person has just erased, while the unfiltered rows are on their way. An
   * empty table then keeps its headers, its filter row and the no-match state
   * rather than turning into the empty state, which would take the focused
   * filter input away mid-edit.
   */
  filtered?: boolean;
  /**
   * Offer a "Colunas" control listing the columns, each with a checkbox. On by
   * default (#443): a listing a person scans and compares should let them
   * narrow it. Pass `false` where it is noise -- a table inside a modal, the
   * result or preview of an upload, a nested detail of another row, or one too
   * narrow for hiding a column to leave anything to read. The control never
   * renders when no column can be hidden.
   */
  columnVisibility?: boolean;
  /**
   * A stable slug naming this table, the same whatever record it shows -- the
   * segurados table on every company page shares one -- under which the
   * columns a person hides through "Colunas" are remembered on the server, per
   * person, across reloads and browsers (#444). Every table showing the same
   * id shares the choice at once. Stored keys that name no column here, or a
   * column that cannot be hidden, are ignored. Without an `id` (or with
   * `columnVisibility` off) the choice lives in memory, for as long as the
   * table is mounted, and nothing is written.
   */
  id?: string;
  /**
   * The rows arrive already sorted and filtered (by the API, say): render them
   * as given and only report sorting and filter changes. Pass `sorting` and
   * `columnFilters` with their callbacks alongside.
   */
  manual?: boolean;
  /** Controlled sorting. Without it the table keeps its own. */
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  /** Controlled filters. Without them the table keeps its own. */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
}) {
  const linked = rowLink !== undefined;
  const columnDefs = useMemo(
    () =>
      columns.map((column, index): ColumnDef<Features, Boxed<TRow>> => {
        const accessor =
          column.accessor ??
          // A manual table holds filter state for a column the screen filters
          // itself; TanStack only holds state for a column with a value.
          (manual && column.filter !== undefined ? () => undefined : undefined);
        const base = {
          id: column.key,
          header: column.header,
          enableHiding: canBeHidden(column, index, linked),
          enableSorting: column.sortable === true,
          enableColumnFilter: column.filter !== undefined,
        };
        if (accessor === undefined) return base;
        return {
          ...base,
          // `undefined` is what `sortUndefined` puts last; `null` is the same
          // absence as far as a reader is concerned.
          accessorFn: (boxed: Boxed<TRow>) => accessor(boxed.row) ?? undefined,
          sortFn: compareValues<TRow>(),
          sortUndefined: 'last',
          filterFn: column.filter?.kind === 'select' ? selectFilter<TRow>() : textFilter<TRow>(),
        };
      }),
    [columns, linked, manual],
  );
  const data = useMemo(() => rows.map((row) => ({ row })), [rows]);

  const [ownSorting, setOwnSorting] = useState<SortingState>([]);
  const [ownFilters, setOwnFilters] = useState<ColumnFiltersState>([]);
  const [ownVisibility, setOwnVisibility] = useState<ColumnVisibilityState>({});
  const currentSorting = sorting ?? ownSorting;
  const currentFilters = columnFilters ?? ownFilters;
  // A table without the control has no way to show a column again, so it
  // applies no stored choice either.
  const stored = useStoredHiddenColumns(columnVisibility ? id : undefined);
  const storedHidden = stored?.hidden;
  const currentVisibility = useMemo(() => {
    if (storedHidden === undefined) return ownVisibility;
    const hidden = new Set(storedHidden);
    return Object.fromEntries(
      columns.flatMap((column, index) =>
        hidden.has(column.key) && canBeHidden(column, index, linked) ? [[column.key, false]] : [],
      ),
    );
  }, [storedHidden, ownVisibility, columns, linked]);

  const table = useTable({
    features,
    columns: columnDefs,
    data,
    getRowId: (_row, index) => String(index),
    state: {
      sorting: currentSorting as TanStackSorting,
      columnFilters: currentFilters as TanStackColumnFilters,
      columnVisibility: currentVisibility,
    },
    onSortingChange: (updater: Updater<TanStackSorting>) => {
      const next = functionalUpdate(updater, currentSorting as TanStackSorting);
      setOwnSorting(next);
      onSortingChange?.(next);
    },
    onColumnFiltersChange: (updater: Updater<TanStackColumnFilters>) => {
      const next = functionalUpdate(updater, currentFilters as TanStackColumnFilters).map(
        ({ id, value }) => ({ id, value: String(value) }),
      );
      setOwnFilters(next);
      onColumnFiltersChange?.(next);
    },
    onColumnVisibilityChange: (updater: Updater<ColumnVisibilityState>) => {
      const next = functionalUpdate(updater, currentVisibility);
      if (stored === null) {
        setOwnVisibility(next);
        return;
      }
      const own = new Set(columns.map((column) => column.key));
      stored.store(
        [
          ...columns
            .filter(
              (column, index) => canBeHidden(column, index, linked) && next[column.key] === false,
            )
            .map((column) => column.key),
          // A key naming a column this table does not have is left as it was:
          // the same table elsewhere may have it.
          ...stored.hidden.filter((key) => !own.has(key)),
        ].slice(0, STORED_KEYS_MAX),
      );
    },
    manualSorting: manual,
    manualFiltering: manual,
    enableMultiSort: false,
    sortDescFirst: false,
  });

  const filtersDeclared = columns.some((column) => column.filter !== undefined);
  const filtering = currentFilters.length > 0;

  if (rows.length === 0 && !filtering && !filtered) {
    return (
      <EmptyState
        title={emptyTitle}
        {...(emptyDescription !== undefined && { description: emptyDescription })}
      />
    );
  }

  const byKey = new Map(columns.map((column) => [column.key, column]));
  const visible = table
    .getVisibleLeafColumns()
    .flatMap((leaf) => byKey.get(leaf.id) ?? [])
    .map((column) => ({ column, state: table.getColumn(column.key) }));
  const bodyRows = table.getRowModel().rows;

  // A menu of disabled checkboxes is worse than no button, and hiding a
  // table's only column leaves nothing to read.
  const columnMenu =
    columnVisibility &&
    columns.length > 1 &&
    columns.some((column, index) => canBeHidden(column, index, linked));
  const toolbar = (filtersDeclared || columnMenu) && (
    <div className="table-toolbar">
      {filtersDeclared && (
        <button
          type="button"
          className="button button--ghost"
          disabled={!filtering}
          onClick={() => {
            table.resetColumnFilters(true);
          }}
        >
          Limpar filtros
        </button>
      )}
      {columnMenu && (
        <ColumnMenu
          columns={columns.map((column, index) => ({
            key: column.key,
            header: column.header,
            hideable: canBeHidden(column, index, linked),
            visible: table.getColumn(column.key)?.getIsVisible() ?? true,
            toggle: (next: boolean) => {
              table.getColumn(column.key)?.toggleVisibility(next);
            },
          }))}
        />
      )}
    </div>
  );

  return (
    <>
      {toolbar}
      <div className="table-wrapper">
        <table className="table">
          <caption className="visually-hidden">{caption}</caption>
          <thead>
            <tr>
              {visible.map(({ column, state }) => {
                const sortable = column.sortable === true && state?.getCanSort() === true;
                const sorted = sortable ? state.getIsSorted() : false;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={column.align === 'end' ? 'table__cell--end' : undefined}
                    {...(column.headerHint !== undefined && { title: column.headerHint })}
                    {...(sortable && {
                      'aria-sort':
                        sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none',
                    })}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className="table__sort"
                        onClick={() => {
                          state.toggleSorting(undefined, false);
                        }}
                      >
                        {column.header}
                        <span
                          className={`table__sort-indicator${sorted === false ? '' : ' table__sort-indicator--active'}`}
                          aria-hidden="true"
                        >
                          {sorted === 'asc' ? '▲' : sorted === 'desc' ? '▼' : '↕'}
                        </span>
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
            {filtersDeclared && (
              <tr className="table__filters">
                {visible.map(({ column, state }) => (
                  <td key={column.key}>
                    {column.filter !== undefined && state?.getCanFilter() === true && (
                      <FilterControl
                        header={column.header}
                        filter={column.filter}
                        value={currentFilters.find((entry) => entry.id === column.key)?.value ?? ''}
                        onChange={(value) => {
                          state.setFilterValue(value);
                        }}
                      />
                    )}
                  </td>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {bodyRows.length === 0 && (
              <tr className="table__no-match">
                <td colSpan={visible.length}>
                  <EmptyState
                    title={noMatchTitle}
                    description={noMatchDescription}
                    {...(noMatchAction !== undefined && { action: noMatchAction })}
                  />
                </td>
              </tr>
            )}
            {bodyRows.map((tableRow) => {
              const { row } = tableRow.original;
              const detail = expansion?.(row) ?? null;
              const to = rowLink?.(row);
              const cells = visible.map(({ column }, columnIndex) => (
                <td
                  key={column.key}
                  className={column.align === 'end' ? 'table__cell--end' : undefined}
                >
                  {to !== undefined && columnIndex === 0 ? (
                    <Link to={to}>{column.render(row)}</Link>
                  ) : (
                    column.render(row)
                  )}
                </td>
              ));
              return (
                <Fragment key={rowKey(row, tableRow.index)}>
                  {to === undefined ? <tr>{cells}</tr> : <LinkRow to={to}>{cells}</LinkRow>}
                  {detail !== null && (
                    <tr className="table__expansion">
                      <td colSpan={visible.length}>{detail}</td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** One column's control in the filter row, named by the column's header. */
function FilterControl({
  header,
  filter,
  value,
  onChange,
}: {
  header: string;
  filter: ColumnFilterKind;
  value: string;
  onChange: (value: string) => void;
}) {
  if (filter.kind === 'select') {
    return (
      <select
        className="field__input table__filter"
        aria-label={header}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        <option value="">Todos</option>
        {filter.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type="search"
      className="field__input table__filter"
      aria-label={header}
      placeholder={filter.placeholder ?? 'Filtrar'}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

/**
 * "Colunas": a disclosure listing every column with a checkbox. A column that
 * cannot be hidden is listed too, checked and disabled, so the list matches
 * the table.
 */
function ColumnMenu({
  columns,
}: {
  columns: readonly {
    key: string;
    header: string;
    hideable: boolean;
    visible: boolean;
    toggle: (visible: boolean) => void;
  }[];
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') setOpen(false);
  };
  return (
    <div className="table-columns" onKeyDown={onKeyDown}>
      <button
        type="button"
        className="button button--secondary"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        Colunas
      </button>
      {open && (
        <fieldset id={menuId} className="table-columns__menu">
          <legend className="visually-hidden">Colunas visíveis</legend>
          {columns.map((column) => (
            <label key={column.key} className="checkbox">
              <input
                type="checkbox"
                checked={column.visible}
                disabled={!column.hideable}
                onChange={(event) => {
                  column.toggle(event.target.checked);
                }}
              />
              {column.header}
            </label>
          ))}
        </fieldset>
      )}
    </div>
  );
}

/**
 * A row that goes where its anchor goes when clicked anywhere else.
 *
 * Its own component so that only a table with `rowLink` needs a router.
 */
function LinkRow({ to, children }: { to: string; children: ReactNode }) {
  const navigate = useNavigate();
  const onClick = (event: MouseEvent<HTMLTableRowElement>) => {
    // The anchor handles its own clicks, modifiers included; any other control
    // in the row is doing something else.
    if ((event.target as Element).closest('a, button, input, select, textarea') !== null) {
      return;
    }
    void navigate(to);
  };
  return (
    <tr className="table__row--link" onClick={onClick}>
      {children}
    </tr>
  );
}
